import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Link as LinkIcon, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export const ShareLinkDialog = ({ open, onOpenChange, type, targetId, title }) => {
  const [links, setLinks] = useState([]);
  const [password, setPassword] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/share-links?target_id=${encodeURIComponent(targetId)}`); setLinks(r.data || []); } catch (e) {}
  };
  useEffect(() => { if (open && targetId) load(); }, [open, targetId]);

  const create = async () => {
    setBusy(true);
    try {
      const payload = { type, target_id: targetId };
      if (password) payload.password = password;
      if (expiresAt) payload.expires_at = new Date(expiresAt).toISOString();
      const r = await api.post('/share-links', payload);
      const url = `${window.location.origin}${r.data.url_path}`;
      try { await navigator.clipboard.writeText(url); toast.success('Link copied to clipboard'); } catch (e) { toast.success('Link created'); }
      setPassword(''); setExpiresAt(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const copy = async (token) => {
    const url = `${window.location.origin}/share/${token}`;
    try { await navigator.clipboard.writeText(url); toast.success('Copied'); } catch (e) { toast.error('Copy failed'); }
  };
  const revoke = async (id) => {
    if (!window.confirm('Revoke this link?')) return;
    try { await api.delete(`/share-links/${id}`); load(); toast.success('Revoked'); } catch (e) { toast.error('Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="share-link-dialog">
        <DialogHeader><DialogTitle className="font-serif">Share {title || type}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Password (optional)</Label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave empty for public" data-testid="share-password-input" />
          </div>
          <div>
            <Label className="text-xs">Expires at (optional)</Label>
            <Input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} data-testid="share-expires-input" />
          </div>
          <Button onClick={create} disabled={busy} data-testid="share-create-button" className="w-full rounded-xl gradient-fab text-white"><LinkIcon className="h-4 w-4 mr-2" />Create share link</Button>
        </div>
        <div className="mt-3 space-y-2" data-testid="share-links-existing">
          {links.length > 0 && <div className="text-xs uppercase tracking-wider text-muted-foreground">Existing links</div>}
          {links.map((l) => (
            <div key={l.id} className={`rounded-xl border p-2 flex items-center gap-2 ${l.revoked ? 'opacity-60' : ''}`} data-testid={`share-link-${l.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono truncate">/share/{l.token}</div>
                <div className="text-[10px] text-muted-foreground">
                  {l.password_hash ? 'Password protected · ' : ''}
                  {l.expires_at ? `Expires ${new Date(l.expires_at).toLocaleDateString()}` : 'No expiry'}
                  {l.revoked ? ' · Revoked' : ''}
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => copy(l.token)} className="rounded-xl" data-testid={`share-copy-${l.id}`}><Copy className="h-4 w-4" /></Button>
              {!l.revoked && <Button size="icon" variant="ghost" onClick={() => revoke(l.id)} className="rounded-xl text-destructive" data-testid={`share-revoke-${l.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl" data-testid="share-close-button">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
